import React from 'react';
import connectToStores from 'alt-utils/lib/connectToStores';
import CallActions from 'actions/CallActions';
import {isInVisibleState} from '@store/calls';
import NoSearchResultsIcon from 'images/search/NoSearchResults.svg';
import SearchingIcon from 'images/search/SearchingIcon.svg';
import Pagination from '@components/pagination';
import CallsListItemContainer from '@components/call-card/search/CallsListItemContainer';
import {scrollToTop} from 'utils/scrollToTop';
import SearchStore from 'stores/SearchStore';
import LibraryStore from 'stores/library/LibraryStore';
import LibraryActions from 'actions/library/LibraryActions';

// This value comes from ElasticSearch's max_result_window size for calls.
// Pagination past this number of results will error out.
const maxNumberOfCallsPerSearch = 10000;

@connectToStores
class CallsListComponent extends React.PureComponent {
  static getStores() {
    return [LibraryStore, SearchStore];
  }

  static getPropsFromStores({isLibrarySearch}) {
    const state = isLibrarySearch ? LibraryStore.getState() : SearchStore.getState();
    return {
      queuedSearch: state.get('queuedSearch'),
      searchLoading: state.get('searchLoading'),
      searchError: state.get('searchError'),
      searchResults: state.get('searchResults')?.results?.filter(r => isInVisibleState(r)),
      searchTotal: state.get('searchTotal'),
      searchPage: state.get('searchPage'),
      missingIndex: state.get('missingIndex'),
      searchFields: state.searchFields,
      searchLimit: state.get('searchLimit'),
    };
  }

  /**
   * Handle page change
   * @param  {number} page Page number. Notice that this number starts from 1
   */
  onChangePage = (_, page) => {
    scrollToTop();
    if (this.props.isLibrarySearch) {
      LibraryActions.changeLibraryPage(page);
    } else {
      CallActions.changePage(page);
    }
  };

  shouldRenderResults(results) {
    return results && !results.isEmpty() && !this.props.searchLoading;
  }

  render() {
    const {
      isLibrarySearch,
      libraryId, // set explicitly on the library search only.
      searchResults,
      searchLimit,
      searchTotal,
      searchPage,
    } = this.props;

    if (!searchResults) {
      return null;
    }

    // Derive maximum number of pages allowed (based on ElasticSearch's window size limit)
    const maxNumOfPages = Math.floor(maxNumberOfCallsPerSearch / searchLimit);
    // Prevent number of pages from exceeding the maximum
    const pageCount = Math.min(Math.ceil(searchTotal / searchLimit), maxNumOfPages);
    // Prevent user from somehow going past the maximum
    const page = Math.min(searchPage, maxNumOfPages);

    return (
      <div className="calls-list-component">
        <SearchMessageContainer
          {...this.props}
          noResults={searchResults?.isEmpty()}
          isLibrarySearch={isLibrarySearch}
        />
        {this.shouldRenderResults(searchResults) && (
          <div>
            {searchResults.toJS().map(fileUpload => (
              <CallsListItemContainer
                key={fileUpload.id}
                fileUpload={fileUpload}
                libraryId={libraryId}
                isLibrarySearch={isLibrarySearch}
              />
            ))}
            <div className="pagination-wrapper">
              <Pagination
                size="large"
                color="primary"
                count={pageCount}
                page={page}
                onChange={this.onChangePage}
              />
              {page === maxNumOfPages && (
                <div className="pagination-hint">Adjust filter to view more calls</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
}

function searchMessage(
  missingIndex,
  searchError,
  searchLoading,
  noResults,
  isLibrarySearch,
  formState
) {
  if (missingIndex) return 'There are no calls in your account yet.';
  if (searchError) return 'There was an error communicating with the server.';
  if (noResults) return noResultsMessage(formState, isLibrarySearch);
  return null;
}

function noResultsMessage(formState, isLibrarySearch) {
  const isFiltered = formState && (formState.tags.length > 0 || formState.callers.length > 0);
  if (isLibrarySearch && isFiltered) return 'No results found for the filters chosen';
  if (isLibrarySearch) return 'This library is currently empty';
  return 'No Search Results Found';
}

function SearchMessageContainer({
  missingIndex,
  searchError,
  searchLoading,
  noResults,
  isLibrarySearch,
  formState,
}) {
  const message = searchMessage(
    missingIndex,
    searchError,
    searchLoading,
    noResults,
    isLibrarySearch,
    formState
  );

  if (message === null) {
    return null;
  }

  return (
    <div className="search-message-container">
      <div className="search-message">
        {searchLoading ? (
          <img alt="search" className="icon" src={SearchingIcon} />
        ) : (
          <img alt="no search results" className="icon" src={NoSearchResultsIcon} />
        )}
        <div className="text">{message}</div>
      </div>
    </div>
  );
}

export default CallsListComponent;
